import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { CheckCircle, Cancel, EventAvailable, FileDownloadOutlined, Tune } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { produtoService } from '@/services/produtoService'
import { baixarCsv, sufixoData } from '@/utils/csv'
import { AjusteReceita, EstadoFinanceiroComissao, LancamentoComissao } from '@/types/comissao'
import { SX_DESTAQUE, useRolarAteDestaque } from '@/hooks/useRolarAteDestaque'
import EmitirNotaDialog from './EmitirNotaDialog'
import { ESTADO_FINANCEIRO, brl, estadoDe } from './estadoFinanceiro'

/**
 * Extrato unificado do Financeiro (Figma 26:339, A15): Competência · Cliente · Produto ·
 * Base · Comissão · Status · Ação. O status é o ciclo Aguardando NF → NF recebida →
 * Programado → Pago, DERIVADO no backend; "Emitir →" abre o registro da nota com upload;
 * "Baixar →" entrega o demonstrativo da linha paga.
 *
 * **Nada é calculado aqui.** A comissão é MANUAL (04/08): o operador lança e confirma; a
 * "Base" (receita sobre a qual a comissão incide) não é registrada na apuração manual e
 * aparece como "—" — regra do contrato: sem dado, traço, não número inventado.
 *
 * O que este painel substituiu: a versão anterior tinha as suas próprias somas
 * ("Disponível para NF" / "Projetado") — hoje os KPIs da página vêm do
 * `resumo-consultor` (A03), a mesma fonte, e a tabela ganhou as colunas do Figma.
 *
 * **Receita Originei (V54, AN-7, Q25).** Ao confirmar, o Financeiro informa a receita da
 * Originei naquela competência — obrigatória, zero aceito, nunca valor de simulação. Ela
 * alimenta a meta de RECEITA. Só o back-office vê a coluna e o "Ajustar receita" (correção
 * append-only); cancelar uma comissão confirmada estorna a receita na competência escolhida.
 * O valor líquido (confirmada + ajustes) vem pronto do backend.
 */

const competencia = (iso: string): string => dayjs(iso).format('MM/YYYY')
/** Mês corrente no formato do `<input type="month">`. */
const mesAtual = (): string => dayjs().format('YYYY-MM')
/** `YYYY-MM` → `YYYY-MM-01`, como o backend espera a competência. */
const diaUm = (mes: string): string => `${mes}-01`
const data = (iso?: string | null): string => (iso ? dayjs(iso).format('DD/MM/YYYY') : '—')

/**
 * P5 (AN-PP5 §3.3, §9; PP5-valr B3): o ESTORNO (cancelamento em competência fechada) e o lançamento que
 * ele anulou vêm com `estadoFinanceiro` ESTORNO/ESTORNADO — nunca "Aguardando NF" (não são comissão a emitir).
 */
const ehEstorno = (l: LancamentoComissao) => l.tipo === 'ESTORNO' || estadoDe(l) === 'ESTORNO'
const rotuloStatus = (l: LancamentoComissao): string => ESTADO_FINANCEIRO[estadoDe(l)].rotulo
/** Recorrência gravada pelo servidor (política do produto/marco); nula = lançamento legado. */
const rotuloRecorrencia = (l: LancamentoComissao): string =>
  l.recorrencia === 'RECORRENTE' ? 'recorrente' : l.recorrencia === 'UNICA' ? 'única' : '—'

interface Props {
  /** Operador/gestor vê confirmar, programar e cancelar; o consultor emite e baixa. */
  podeOperar: boolean
  /** Contador para o pai forçar recarga (ex.: nota paga/recusada na outra aba). */
  versao?: number
  /**
   * Deep link da Central (L7 / Q40, `?lancamento=`): destaca e rola até esta linha. Se ela
   * não estiver no extrato (que já vem no escopo do usuário), avisa — nada de outro consultor.
   */
  lancamentoDestacado?: string | null
  /** Deep link da Central (`?estado=AGUARDANDO_NF`): destaca todas as linhas nesse estado. */
  estadoDestacado?: EstadoFinanceiroComissao | null
  /**
   * Linha em NF_COM_PENDENCIA: abre a nota na aba de NF (`/financeiro?aba=nf&nota={id}`),
   * o mesmo destino do item NF_PENDENCIA da Central. Sem o callback, o link navega pelo href.
   */
  onAbrirNota?: (notaFiscalId: string) => void
}

const ComissoesPanel: React.FC<Props> = ({
  podeOperar,
  versao = 0,
  lancamentoDestacado = null,
  estadoDestacado = null,
  onAbrirNota,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [lancamentos, setLancamentos] = useState<LancamentoComissao[]>([])
  const [produtos, setProdutos] = useState<Record<string, string>>({})
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [emitindo, setEmitindo] = useState<LancamentoComissao | null>(null)
  const [dialogoEmitir, setDialogoEmitir] = useState(false)

  const [confirmando, setConfirmando] = useState<LancamentoComissao | null>(null)
  const [valorConfirmado, setValorConfirmado] = useState('')
  const [programando, setProgramando] = useState<LancamentoComissao | null>(null)
  const [dataPrevista, setDataPrevista] = useState('')
  const [cancelando, setCancelando] = useState<LancamentoComissao | null>(null)
  const [motivo, setMotivo] = useState('')
  const [receitaOriginei, setReceitaOriginei] = useState('')
  const [competenciaEstorno, setCompetenciaEstorno] = useState(mesAtual())
  const [ajustando, setAjustando] = useState<LancamentoComissao | null>(null)
  const [ajusteMes, setAjusteMes] = useState(mesAtual())
  const [ajusteValor, setAjusteValor] = useState('')
  const [ajusteMotivo, setAjusteMotivo] = useState('')
  const [historico, setHistorico] = useState<AjusteReceita[]>([])
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setLancamentos(await comissaoService.extrato())
    } catch {
      setErro('Não foi possível carregar as comissões.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar, versao])

  // Nome do produto é de outro módulo: melhor esforço, e "—" quando não vier.
  useEffect(() => {
    let ativo = true
    produtoService
      .listar(0, 100)
      .then((pagina) => {
        if (!ativo) return
        const mapa: Record<string, string> = {}
        for (const p of pagina?.content ?? []) mapa[p.id] = p.nome
        setProdutos(mapa)
      })
      .catch(() => {
        /* sem nome de produto a coluna mostra "—" */
      })
    return () => {
      ativo = false
    }
  }, [])

  const disponiveis = useMemo(
    () => lancamentos.filter((l) => l.disponivelParaNota),
    [lancamentos]
  )

  // Deep link (L7 / Q40): linha(s) que a Central mandou abrir.
  const ehDestacado = (l: LancamentoComissao) =>
    l.id === lancamentoDestacado || (estadoDestacado != null && estadoDe(l) === estadoDestacado)
  const primeiroDestacado = lancamentos.find(ehDestacado)
  useRolarAteDestaque(primeiroDestacado ? `lancamento-${primeiroDestacado.id}` : null, !carregando)
  const lancamentoAusente =
    !!lancamentoDestacado && !carregando && !erro && !lancamentos.some((l) => l.id === lancamentoDestacado)

  const produtoDe = (l: LancamentoComissao) =>
    (l.produtoId && produtos[l.produtoId]) || '—'

  /** "Estorno de mm/aaaa": a competência (ou referência) do original — busca na própria lista, sem conta. */
  const legendaEstorno = (l: LancamentoComissao): string => {
    const original = lancamentos.find((o) => o.id === l.estornaLancamentoId)
    return original ? `Estorno de ${competencia(original.competenciaReferencia ?? original.competencia)}` : 'Estorno'
  }
  /** "Estornado em mm/aaaa": a competência do ESTORNO deste lançamento, quando ele está no extrato. */
  const legendaEstornado = (l: LancamentoComissao): string => {
    const estorno = lancamentos.find((o) => ehEstorno(o) && o.estornaLancamentoId === l.id)
    return estorno ? `Estornado em ${competencia(estorno.competencia)}` : 'Estornado'
  }

  const abrirEmitir = (l: LancamentoComissao | null) => {
    setEmitindo(l)
    setDialogoEmitir(true)
  }

  /**
   * Demonstrativo da linha paga. Hoje é CSV — a plataforma não gera PDF; a única
   * exportação existente no projeto é `baixarCsv`. Registrado no relatório do A15.
   */
  const baixarDemonstrativo = (l: LancamentoComissao) =>
    baixarCsv(
      `demonstrativo-${competencia(l.competencia).replace('/', '-')}-${l.numeroReserva ?? l.id}`,
      ['Competência', 'Cliente', 'Produto', 'Reserva', 'Comissão', 'Nota fiscal', 'Data prevista', 'Data do pagamento', 'Status'],
      [[
        competencia(l.competencia), l.razaoSocial ?? '', produtoDe(l), `RES-${l.numeroReserva ?? ''}`,
        l.valorEfetivo, l.notaFiscalNumero ?? '', data(l.dataPrevistaPagamento), data(l.dataPagamento),
        rotuloStatus(l),
      ]]
    )

  const exportarExtrato = () =>
    baixarCsv(
      `comissoes-${sufixoData()}`,
      ['Competência', 'Referência', 'Cliente', 'Produto', 'Reserva', 'Tipo', 'Projetado', 'Comissão', 'Status', 'Nota fiscal', 'Data prevista', 'Pagamento', 'Recorrência', 'Competência fechada', 'Motivo do cancelamento'],
      lancamentos.map((l) => [
        competencia(l.competencia), l.competenciaReferencia ? competencia(l.competenciaReferencia) : '',
        l.razaoSocial ?? '', produtoDe(l), `RES-${l.numeroReserva ?? ''}`, ehEstorno(l) ? 'estorno' : 'lançamento',
        l.valorProjetado, l.valorEfetivo, rotuloStatus(l),
        l.notaFiscalNumero ?? '', data(l.dataPrevistaPagamento), data(l.dataPagamento),
        rotuloRecorrencia(l), l.competenciaFechada ? 'sim' : 'não', l.motivoCancelamento ?? '',
      ])
    )

  // Receita Originei: obrigatória, número ≥ 0 (zero aceito). O botão só habilita com ela válida.
  const receitaValida =
    receitaOriginei.trim() !== '' &&
    Number.isFinite(Number(receitaOriginei)) &&
    Number(receitaOriginei) >= 0

  const confirmar = async () => {
    if (!confirmando || !receitaValida) return
    setSalvando(true)
    try {
      // Campo vazio mantém a projeção — não force o operador a redigitar o mesmo número.
      const valor = valorConfirmado.trim() === '' ? undefined : Number(valorConfirmado)
      await comissaoService.confirmar(confirmando.id, {
        valorConfirmado: valor,
        receitaOrigineiConfirmada: Number(receitaOriginei),
      })
      enqueueSnackbar('Recebimento confirmado — comissão aguardando NF do consultor.', {
        variant: 'success',
      })
      setConfirmando(null)
      setValorConfirmado('')
      setReceitaOriginei('')
      await carregar()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao confirmar o recebimento.',
        { variant: 'error' }
      )
    } finally {
      setSalvando(false)
    }
  }

  const abrirAjuste = (l: LancamentoComissao) => {
    setAjustando(l)
    setAjusteMes(mesAtual())
    setAjusteValor('')
    setAjusteMotivo('')
    setHistorico([])
    comissaoService
      .ajustesReceita(l.id)
      .then((lista) => setHistorico(lista ?? []))
      .catch(() => {
        /* sem histórico, o diálogo ainda permite ajustar */
      })
  }

  const ajusteValido =
    ajusteValor.trim() !== '' &&
    Number.isFinite(Number(ajusteValor)) &&
    Number(ajusteValor) !== 0 &&
    ajusteMotivo.trim() !== '' &&
    ajusteMes !== ''

  const ajustar = async () => {
    if (!ajustando || !ajusteValido) return
    setSalvando(true)
    try {
      await comissaoService.ajustarReceita(ajustando.id, {
        competencia: diaUm(ajusteMes),
        valor: Number(ajusteValor),
        motivo: ajusteMotivo.trim(),
      })
      enqueueSnackbar('Receita Originei ajustada.', { variant: 'success' })
      setAjustando(null)
      await carregar()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao ajustar a receita.',
        { variant: 'error' }
      )
    } finally {
      setSalvando(false)
    }
  }

  const programar = async () => {
    if (!programando || !dataPrevista) return
    setSalvando(true)
    try {
      await comissaoService.programar(programando.id, { dataPrevista })
      enqueueSnackbar('Pagamento programado.', { variant: 'success' })
      setProgramando(null)
      setDataPrevista('')
      await carregar()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao programar o pagamento.',
        { variant: 'error' }
      )
    } finally {
      setSalvando(false)
    }
  }

  // Cancelar uma confirmada com receita líquida ≠ 0 estorna a receita (backend); a tela avisa.
  const estornaReceita =
    !!cancelando &&
    cancelando.status === 'CONFIRMADA' &&
    cancelando.receitaOrigineiLiquida != null &&
    Number(cancelando.receitaOrigineiLiquida) !== 0

  // P5: competência fechada — a linha não muda; o backend cria um ESTORNO na competência aberta.
  const emCompetenciaFechada = !!cancelando?.competenciaFechada
  const pedeCompetenciaEstorno = estornaReceita || emCompetenciaFechada

  const cancelar = async () => {
    if (!cancelando) return
    setSalvando(true)
    try {
      const resposta = await comissaoService.cancelar(
        cancelando.id,
        motivo.trim(),
        pedeCompetenciaEstorno ? diaUm(competenciaEstorno) : undefined
      )
      enqueueSnackbar(
        resposta?.tipo === 'ESTORNO'
          ? `Competência fechada: estorno de ${brl(resposta.valorEfetivo)} lançado em ${competencia(resposta.competencia)}.`
          : 'Comissão cancelada.',
        { variant: 'success' }
      )
      setCancelando(null)
      setMotivo('')
      await carregar()
    } catch (e) {
      enqueueSnackbar((e as { message?: string })?.message ?? 'Falha ao cancelar a comissão.', { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando comissões" />
      </Box>
    )
  }

  return (
    <Box>
      {erro && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button size="small" color="inherit" onClick={() => void carregar()}>
              Tentar de novo
            </Button>
          }
        >
          {erro}
        </Alert>
      )}

      {lancamentoAusente && (
        <Alert severity="info" sx={{ mb: 2 }}>
          O lançamento indicado não está no seu extrato — pode já ter sido resolvido.
        </Alert>
      )}

      {lancamentos.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Nenhuma comissão lançada
          </Typography>
          <Typography variant="body2" color="textSecondary">
            As comissões são lançadas pelo operador a partir da etapa de negociação.
          </Typography>
        </Card>
      ) : (
        <Card>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, pb: 0 }}>
            <Typography variant="caption" color="textSecondary">
              Base: não registrada na apuração manual — a comissão é lançada pelo operador.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              onClick={exportarExtrato}
            >
              Exportar CSV
            </Button>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Comissões">
              <TableHead>
                <TableRow>
                  <TableCell>Competência</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Base</TableCell>
                  <TableCell align="right">Comissão</TableCell>
                  {podeOperar && <TableCell align="right">Receita Originei</TableCell>}
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lancamentos.map((l) => {
                  const estado = estadoDe(l)
                  const chip = ESTADO_FINANCEIRO[estado]
                  const estorno = ehEstorno(l)
                  // Estorno e estornado não têm ação: o par é fechado pelo backend (P5).
                  const somenteLeitura = estorno || !!l.estornado || estado === 'ESTORNADO'
                  const detalhe =
                    estado === 'PROGRAMADO'
                      ? `Previsto para ${data(l.dataPrevistaPagamento)}`
                      : estado === 'PAGO'
                        ? `Pago em ${data(l.dataPagamento)}`
                        : estado === 'NF_RECEBIDA' && l.notaFiscalNumero
                          ? `Nota ${l.notaFiscalNumero}`
                          : (l.motivoCancelamento ?? '')
                  return (
                    <TableRow
                      key={l.id}
                      hover
                      id={`lancamento-${l.id}`}
                      data-destacado={ehDestacado(l) ? 'true' : undefined}
                      sx={ehDestacado(l) ? SX_DESTAQUE : undefined}
                    >
                      <TableCell>{competencia(l.competencia)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{l.razaoSocial ?? `RES-${l.numeroReserva ?? '—'}`}</TableCell>
                      <TableCell>{produtoDe(l)}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {brl(l.valorEfetivo)}
                      </TableCell>
                      {podeOperar && (
                        <TableCell align="right">
                          {l.receitaOrigineiLiquida != null ? brl(l.receitaOrigineiLiquida) : '—'}
                          {l.receitaAjustada && (
                            <Chip size="small" label="ajustada" variant="outlined" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {estorno ? (
                          <>
                            <Chip size="small" label="Estorno" color="error" variant="outlined" />
                            <Typography variant="caption" color="textSecondary" display="block">
                              {legendaEstorno(l)}
                            </Typography>
                          </>
                        ) : l.estornado || estado === 'ESTORNADO' ? (
                          <>
                            <Chip size="small" label="Estornado" variant="outlined" />
                            <Typography variant="caption" color="textSecondary" display="block">
                              {legendaEstornado(l)}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Tooltip title={detalhe} disableHoverListener={!detalhe}>
                              <Chip
                                size="small"
                                label={chip.rotulo}
                                color={chip.cor}
                                variant={estado === 'CANCELADA' ? 'outlined' : 'filled'}
                              />
                            </Tooltip>
                            {detalhe && estado !== 'CANCELADA' && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {detalhe}
                              </Typography>
                            )}
                          </>
                        )}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {l.competenciaReferencia && (
                            <Chip size="small" variant="outlined" label={`Ref. ${competencia(l.competenciaReferencia)}`} />
                          )}
                          {l.competenciaFechada && (
                            <Chip size="small" variant="outlined" label="Competência fechada" />
                          )}
                          {l.recorrencia === 'RECORRENTE' && !estorno && estado !== 'CANCELADA' && (
                            <Chip size="small" label="Recorrente" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {!somenteLeitura && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                          {estado === 'AGUARDANDO_NF' && (
                            <Button size="small" variant="text" onClick={() => abrirEmitir(l)}>
                              Emitir →
                            </Button>
                          )}
                          {estado === 'NF_COM_PENDENCIA' && l.notaFiscalId && (
                            <Button
                              size="small"
                              variant="text"
                              href={`/financeiro?aba=nf&nota=${encodeURIComponent(l.notaFiscalId)}`}
                              onClick={(e: React.MouseEvent) => {
                                if (!onAbrirNota || !l.notaFiscalId) return
                                e.preventDefault()
                                onAbrirNota(l.notaFiscalId)
                              }}
                            >
                              Ver pendência da NF →
                            </Button>
                          )}
                          {estado === 'PAGO' && (
                            <Button size="small" variant="text" onClick={() => baixarDemonstrativo(l)}>
                              Baixar →
                            </Button>
                          )}
                          {podeOperar && estado === 'PROJETADA' && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<CheckCircle />}
                              onClick={() => {
                                setConfirmando(l)
                                setValorConfirmado('')
                                setReceitaOriginei('')
                              }}
                            >
                              Confirmar
                            </Button>
                          )}
                          {podeOperar && estado === 'NF_RECEBIDA' && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<EventAvailable />}
                              onClick={() => {
                                setProgramando(l)
                                setDataPrevista('')
                              }}
                            >
                              Programar
                            </Button>
                          )}
                          {podeOperar && l.status === 'CONFIRMADA' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Tune />}
                              onClick={() => abrirAjuste(l)}
                            >
                              Ajustar receita
                            </Button>
                          )}
                          {podeOperar && estado !== 'CANCELADA' && estado !== 'PAGO' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => {
                                setCancelando(l)
                                setMotivo('')
                                setCompetenciaEstorno(mesAtual())
                              }}
                            >
                              Cancelar
                            </Button>
                          )}
                        </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <EmitirNotaDialog
        aberto={dialogoEmitir}
        disponiveis={disponiveis}
        preSelecionada={emitindo}
        onFechar={() => setDialogoEmitir(false)}
        onRegistrada={() => void carregar()}
      />

      <Dialog open={confirmando !== null} onClose={() => setConfirmando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Confirmar recebimento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            A Originei recebeu a comissão desta operação. Confirmar torna o valor devido ao
            consultor, que passa a poder emitir a nota fiscal.
          </Typography>
          <TextField
            label="Valor apurado"
            type="number"
            value={valorConfirmado}
            onChange={(e) => setValorConfirmado(e.target.value)}
            placeholder={String(confirmando?.valorProjetado ?? '')}
            helperText="Deixe em branco para manter o valor projetado."
            fullWidth
          />
          <TextField
            label="Receita Originei (R$)"
            type="number"
            required
            value={receitaOriginei}
            onChange={(e) => setReceitaOriginei(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={receitaOriginei.trim() !== '' && !receitaValida}
            helperText={`Receita da Originei nesta operação, informada pelo Financeiro (zero é aceito). Entra na meta de receita na competência ${confirmando ? competencia(confirmando.competencia) : ''}. Não use valor de simulação.`}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmando(null)}>Voltar</Button>
          <Button
            variant="contained"
            onClick={() => void confirmar()}
            disabled={salvando || !receitaValida}
          >
            {salvando ? 'Confirmando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={programando !== null} onClose={() => setProgramando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Programar pagamento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Nota {programando?.notaFiscalNumero ?? ''} recebida. Informe a data em que o
            Financeiro vai pagar — o consultor passa a ver "Programado".
          </Typography>
          <TextField
            label="Data prevista"
            type="date"
            value={dataPrevista}
            onChange={(e) => setDataPrevista(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgramando(null)}>Voltar</Button>
          <Button
            variant="contained"
            onClick={() => void programar()}
            disabled={salvando || !dataPrevista}
          >
            {salvando ? 'Programando…' : 'Programar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelando !== null} onClose={() => setCancelando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Cancelar comissão</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Use quando a Originei deixar de receber — rescisão contratual, inadimplência do
            cliente. O consultor vê o motivo no extrato.
          </Typography>
          <TextField
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: cliente inadimplente desde junho."
            multiline
            minRows={2}
            fullWidth
          />
          {emCompetenciaFechada && cancelando && (
            <Alert severity="info" sx={{ mt: 2 }}>
              A competência {competencia(cancelando.competencia)} está fechada: esta linha não muda. O
              cancelamento cria um estorno (valor negativo) na competência abaixo, e o consultor vê
              os dois no extrato.
            </Alert>
          )}
          {estornaReceita && cancelando && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              A receita Originei desta comissão ({brl(Number(cancelando.receitaOrigineiLiquida))})
              será estornada na competência abaixo. O mês original (
              {competencia(cancelando.competencia)}) não muda.
            </Alert>
          )}
          {pedeCompetenciaEstorno && cancelando && (
            <TextField
              label="Competência do estorno"
              type="month"
              value={competenciaEstorno}
              onChange={(e) => setCompetenciaEstorno(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={
                emCompetenciaFechada
                  ? 'Padrão: mês corrente. Uma competência aberta; se não valer, o backend usa a primeira aberta.'
                  : 'Padrão: mês corrente. Entre a competência da comissão e o mês atual.'
              }
              fullWidth
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelando(null)}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void cancelar()}
            disabled={salvando || !motivo.trim() || (pedeCompetenciaEstorno && !competenciaEstorno)}
          >
            {salvando ? 'Cancelando…' : 'Cancelar comissão'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ajustando !== null} onClose={() => setAjustando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Ajustar receita Originei</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Receita atual: {ajustando?.receitaOrigineiLiquida != null
              ? brl(Number(ajustando.receitaOrigineiLiquida))
              : 'não informada'}{' '}
            (competência {ajustando ? competencia(ajustando.competencia) : ''}). O ajuste não
            altera o valor confirmado nem o mês original: entra na meta de receita na competência
            escolhida.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Competência do ajuste"
              type="month"
              value={ajusteMes}
              onChange={(e) => setAjusteMes(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Valor do ajuste (R$)"
              type="number"
              value={ajusteValor}
              onChange={(e) => setAjusteValor(e.target.value)}
              helperText="Positivo para acrescentar, negativo para reduzir."
              fullWidth
            />
            <TextField
              label="Motivo do ajuste"
              value={ajusteMotivo}
              onChange={(e) => setAjusteMotivo(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              multiline
              minRows={2}
              required
              fullWidth
            />
          </Stack>
          {historico.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Histórico</Typography>
              {historico.map((a) => (
                <Typography key={a.id} variant="caption" display="block" color="textSecondary">
                  {competencia(a.competencia)} · {a.tipo === 'ESTORNO' ? 'Estorno' : 'Correção'} ·{' '}
                  {brl(a.valor)} · {a.motivo}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAjustando(null)}>Voltar</Button>
          <Button
            variant="contained"
            onClick={() => void ajustar()}
            disabled={salvando || !ajusteValido}
          >
            {salvando ? 'Ajustando…' : 'Registrar ajuste'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ComissoesPanel
